/**
 * Typed API client. The browser talks ONLY to the CoreFinOps API — never
 * directly to Postgres. Auth is carried as a bearer token (the Cognito access
 * token, refreshed on demand); the active tenant is sent as a header the API
 * resolves against DB-backed membership. A 401 signals an expired/invalid
 * session and is surfaced to the auth layer.
 */

export interface ApiClientConfig {
  readonly baseUrl: string;
  /** Access token, or a provider that returns a fresh one (null = signed out). */
  readonly token: string | (() => Promise<string | null>);
  /** Active tenant id (resolved against membership by the API). */
  readonly tenantId: string;
  /** Called when the API answers 401 (session expired / revoked). */
  readonly onUnauthorized?: () => void;
}

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export class ApiClientError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
    this.name = "ApiClientError";
  }
}

export class ApiClient {
  constructor(private readonly cfg: ApiClientConfig) {}

  private url(path: string): URL {
    // baseUrl may carry a path prefix (e.g. https://api.example.com/api).
    return new URL(this.cfg.baseUrl.replace(/\/$/, "") + path, globalThis.location?.href ?? "http://localhost");
  }

  async get<T>(path: string, query?: Record<string, string | undefined>): Promise<T> {
    const url = this.url(path);
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v !== undefined && v !== "") url.searchParams.set(k, v);
    }
    return this.request<T>("GET", url.toString());
  }

  async post<T>(path: string, body: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>("POST", this.url(path).toString(), body, headers);
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>("PUT", this.url(path).toString(), body);
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>("DELETE", this.url(path).toString());
  }

  private async request<T>(method: string, url: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
    const token = typeof this.cfg.token === "string" ? this.cfg.token : await this.cfg.token();
    if (!token) {
      this.cfg.onUnauthorized?.();
      throw new ApiClientError(401, "unauthenticated", "Your session has ended. Please sign in again.");
    }
    const res = await fetch(url, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        "x-corefinops-tenant": this.cfg.tenantId,
        accept: "application/json",
        ...(extraHeaders ?? {}),
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? (JSON.parse(text) as unknown) : undefined;
    } catch {
      parsed = undefined;
    }
    if (res.status === 401) this.cfg.onUnauthorized?.();
    if (!res.ok) {
      const err = parsed as ApiErrorBody | undefined;
      throw new ApiClientError(res.status, err?.error?.code ?? "unknown", err?.error?.message ?? res.statusText);
    }
    return parsed as T;
  }
}

/** Response types mirrored from the API (kept in sync with services/api). */
export interface OverviewKpis {
  applications: number;
  workloads: number;
  awsAccounts: number;
  /** Primary-currency scalars (legacy). Prefer the *ByCurrency fields below. */
  allocatedMonthly: string | null;
  unallocatedMonthly: string | null;
  currency: string | null;
  openFindings: number;
  openActions: number;
  verifiedSavings: string | null;
  /** Currency-safe aggregates (never summed across currencies — Req §4.1). */
  allocatedByCurrency: SerializedMoney[];
  unallocatedByCurrency: SerializedMoney[];
  verifiedSavingsByCurrency: SerializedMoney[];
  /** True when any aggregate spans more than one currency — UI must not combine. */
  multipleCurrencies: boolean;
}

export interface PortfolioRowDto {
  workloadId: string;
  workloadName: string;
  applicationId?: string;
  applicationName?: string;
  businessUnit?: string;
  product?: string;
  environment?: string;
  strategy?: string;
  readinessStatus?: string;
  readinessScore?: number;
  migrationStatus?: string;
  wave?: string;
  owner?: string;
  blockers: string[];
}

export interface PortfolioResponse {
  rows: PortfolioRowDto[];
  groups?: Array<{ key: string; count: number }>;
}

export interface SerializedMoney {
  amount: string;
  currency: string;
}

export interface AssessmentResponse {
  workloadId: string;
  latest: {
    version: number;
    recommendedStrategy: string | null;
    overriddenStrategy: string | null;
    effectiveStrategy: string | null;
    dependencyConfidence: string | null;
    confidence: string | null;
    assumptions: string | null;
  } | null;
  versions: Array<{ version: number; recommendedStrategy: string | null; overriddenStrategy: string | null }>;
}

export interface ReadinessResponse {
  workloadId: string;
  score: number;
  status: string;
  decision: "move" | "hold";
  blockers: string[];
  missingData: string[];
  remediation: string[];
  rationale: string;
  dependencyConfidence: string;
}

export interface WaveMetricsDto {
  waveId: string;
  name: string;
  status: string | null;
  owner: string | null;
  targetCutover: string | null;
  workloadCount: number;
  readyCount: number;
  heldCount: number;
  currentMonthlyCost: SerializedMoney;
  projectedMonthlyCost: SerializedMoney;
  transitionCost: SerializedMoney;
  projectedMonthlySavings: SerializedMoney;
  riskScore: number;
  hasHeldWorkloads: boolean;
}

export interface WavesResponse {
  waves: WaveMetricsDto[];
  currency: string;
}

export interface EconomicsResponse {
  workload: { id: string; name: string; applicationId: string; environment: string | null };
  currency: string;
  currentMonthlyRunRate: SerializedMoney;
  projectedMonthlyRunRate: SerializedMoney;
  transitionCost: SerializedMoney;
  monthlySaving: SerializedMoney;
  annualSaving: SerializedMoney;
  oneYearTco: SerializedMoney;
  threeYearTco: SerializedMoney;
  paybackMonths: number | null;
  cumulativeValue: SerializedMoney;
  confidenceLabel: string | null;
  savingCategory: string;
  assumptions: string[];
  trace: {
    currentBlocks: Array<{ label: string; monthly: string; oneTime: string }>;
    transitionBlocks: Array<{ label: string; monthly: string; oneTime: string }>;
    targetBlocks: Array<{ label: string; monthly: string; oneTime: string }>;
    horizonMonths: number;
    notes: string[];
  };
  inputsUsed: Record<string, boolean>;
}

// --- Dual-Run (Operations) --------------------------------------------------
export interface DualRunAlertDto {
  type: "overdue_overlap" | "wave_overlap_budget_exceeded" | "early_aws_spend" | "source_not_decreasing";
  workloadId: string;
  applicationId: string | null;
  owner: string | null;
  amount: SerializedMoney;
  evidence: string;
  dates: Record<string, string>;
  confidenceLabel: "Actual" | "Estimated";
}

export interface DualRunResponse {
  workloadId: string;
  applicationId: string;
  name: string;
  currency: string;
  isDualRunning: boolean;
  sourceMonthlyCost: SerializedMoney;
  targetMonthlyCost: SerializedMoney;
  // Overlap economics are null when the analysis is incomplete (missing dates)
  // or a currency mismatch prevents safe math — never fabricated zeros.
  overlapMonthlyCost: SerializedMoney | null;
  plannedOverlapDays: number | null;
  actualOverlapDays: number | null;
  excessOverlapDays: number | null;
  daysAfterPlannedCutover: number | null;
  avoidableSourceCost: SerializedMoney | null;
  plannedCutover: string | null;
  actualCutover: string | null;
  overlapStart: string | null;
  wave: { waveId: string; name: string; targetCutover: string | null } | null;
  waveOverlapBudget: SerializedMoney | null;
  alerts: DualRunAlertDto[];
  noCutover: boolean;
  analysisComplete: boolean;
  missingInputs: string[];
  currencyMismatch: { source: string; target: string } | null;
}

export interface DualRunListItem {
  workloadId: string;
  applicationId: string;
  name: string;
  owner: string | null;
  plannedCutover: string | null;
  overlapStart: string | null;
  daysAfterPlannedCutover: number | null;
  overlapMonthlyCost: SerializedMoney | null;
  avoidableSourceCost: SerializedMoney | null;
  alertCount: number;
  analysisComplete: boolean;
  currencyMismatch: boolean;
}

export interface DualRunListResponse {
  items: DualRunListItem[];
  currency: string;
}

// --- Decommission (Operations) ----------------------------------------------
export interface DecommissionListRow {
  decommission_id: string;
  workload_id: string;
  technical_shutdown_state: string;
  financial_confirmation_state: string;
  owner: string | null;
  monthly_cost_removed: string | null;
  monthly_cost_removed_currency: string | null;
  target_date: string | null;
}

export interface DecommissionListResponse {
  records: DecommissionListRow[];
}

export interface DecommissionDetailResponse {
  workloadId: string;
  applicationId: string;
  name: string;
  currency: string;
  technicalShutdownState: string;
  financialConfirmationState: string;
  owner: string | null;
  targetDate: string | null;
  actualDate: string | null;
  monthlyCostRemoved: SerializedMoney | null;
  strandedMonthlyCost: SerializedMoney;
  cumulativeStrandedCost: SerializedMoney;
  daysSinceShutdown: number;
  daysOverdue: number;
  assetsRemaining: number;
  blocker: string | null;
  blockerOwner: string | null;
  isBlocked: boolean;
  evidence: string | null;
  financialConfirmationGate: { allowed: boolean; reason: string };
  isFinanciallyConfirmed: boolean;
  confirmationIntegrityWarning: string | null;
  acceptedException: { exceptionId: string; reason: string; approvedBy: string | null; expiresAt: string | null } | null;
  noRecord: boolean;
}

// --- Intelligence / Findings ------------------------------------------------
export interface FindingListRow {
  finding_id: string;
  source: string;
  type: string;
  severity: string;
  status: string;
  classification: string | null;
  observed_condition: string;
  impact_amount: string | null;
  impact_currency: string | null;
  application_id: string | null;
  resource_id: string | null;
  workload_id: string | null;
  first_seen_at: string;
}

export interface FindingsListResponse {
  findings: FindingListRow[];
}

export interface FindingDetailResponse {
  finding: {
    findingId: string;
    source: string;
    type: string;
    severity: string;
    status: string;
    classification: string | null;
    observedCondition: string;
    evidence: Record<string, unknown>;
    impact: SerializedMoney | null;
    scope: {
      accountId: string | null;
      resourceId: string | null;
      applicationId: string | null;
      environmentId: string | null;
      workloadId: string | null;
    };
    wellArchitected: { pillar: string | null; reference: string | null };
    firstSeenAt: string;
    lastSeenAt: string;
    resolvedAt: string | null;
  };
  recommendations: Array<{
    recommendationId: string;
    title: string;
    category: string | null;
    source: string;
    status: string | null;
    confidence: string | null;
    risk: string | null;
    expectedSavings: SerializedMoney | null;
    owner: string | null;
  }>;
  actions: Array<{
    actionId: string;
    recommendationId: string;
    title: string;
    status: string;
    owner: string | null;
    plannedDate: string | null;
    actualDate: string | null;
  }>;
}

// --- Optimize (recommendations + commitments) -------------------------------
export interface RecommendationListItem {
  recommendationId: string;
  source: string;
  category: string | null;
  title: string;
  expectedSavings: SerializedMoney | null;
  confidence: string | null;
  risk: string | null;
  status: string | null;
  owner: string | null;
  workloadId: string | null;
  applicationId: string | null;
  resourceId: string | null;
  hasFinding: boolean;
}

export interface RecommendationsListResponse {
  recommendations: RecommendationListItem[];
  summary: {
    total: number;
    quantified: number;
    expectedMonthlySavingsByCurrency: SerializedMoney[];
    multipleCurrencies: boolean;
  };
}

export interface RecommendationDetailResponse {
  recommendationId: string;
  source: string;
  category: string | null;
  title: string;
  description: string | null;
  evidence: string | null;
  expectedSavings: SerializedMoney | null;
  confidence: string | null;
  risk: string | null;
  status: string | null;
  owner: string | null;
  scope: { applicationId: string | null; workloadId: string | null; resourceId: string | null };
  finding: { findingId: string; type: string; severity: string; status: string; observedCondition: string } | null;
  actions: Array<{ actionId: string; title: string; status: string; owner: string | null; plannedDate: string | null; actualDate: string | null }>;
}

export interface CommitmentDto {
  snapshotId: string;
  accountId: string | null;
  commitmentType: string;
  billingPeriod: string;
  committed: SerializedMoney;
  used: SerializedMoney;
  eligibleOnDemand: SerializedMoney;
  unused: SerializedMoney;
  utilization: string | null;
  coverage: string | null;
  provenance: string;
}

export interface CommitmentsResponse {
  snapshots: CommitmentDto[];
  billingPeriod: string | null;
  summary: {
    committedByCurrency: SerializedMoney[];
    unusedByCurrency: SerializedMoney[];
    multipleCurrencies: boolean;
  };
}

// --- Governance -------------------------------------------------------------
export interface CompletenessMetricDto {
  dimension: string;
  numerator: number;
  denominator: number;
  percentage: string;
  unattributedMonthly?: SerializedMoney;
  reasons: Array<{ reason: string; count: number; monthly?: SerializedMoney }>;
}

export interface GovernanceOverviewResponse {
  currency: string;
  completeness: {
    costAllocationReadiness: CompletenessMetricDto;
    applicationMapping: CompletenessMetricDto;
    ownership: CompletenessMetricDto;
    environmentClassification: CompletenessMetricDto;
    workloadMapping: CompletenessMetricDto;
  };
  unallocatedMonthly: SerializedMoney | null;
  budgetsOverThreshold: number;
  openPolicyFindings: number;
  otherCurrencies: string[];
}

export interface MetadataCompletenessResponse {
  currency: string;
  resourceCount: number;
  metrics: CompletenessMetricDto[];
  otherCurrencies: string[];
}

export interface BudgetStatusDto {
  budgetId: string;
  name: string;
  scopeType: string;
  scopeId: string | null;
  periodStart: string;
  periodEnd: string;
  amount: SerializedMoney;
  actual: SerializedMoney | null;
  forecast: SerializedMoney | null;
  variance: SerializedMoney | null;
  variancePct: string | null;
  consumedPct: string | null;
  breachedThreshold: number | null;
  forecastBreachThreshold: number | null;
  thresholds: number[];
  owner: string | null;
  currencyMismatch: { budget: string; actual: string } | null;
  noActual: boolean;
}

export interface BudgetsResponse {
  budgets: BudgetStatusDto[];
}

export interface PolicyFindingDto {
  findingId: string;
  type: string;
  severity: string;
  status: string;
  classification: string | null;
  observedCondition: string;
  impact: SerializedMoney | null;
  applicationId: string | null;
  resourceId: string | null;
  workloadId: string | null;
  firstSeenAt: string;
}

export interface GovernancePoliciesResponse {
  policies: PolicyFindingDto[];
}

// --- Value Ledger -----------------------------------------------------------
export interface ValueLedgerEntryDto {
  ledgerEntryId: string;
  valueType: string;
  workloadId: string | null;
  verificationStatus: string;
  attributionConfidence: string;
  expectedValue: SerializedMoney | null;
  realizedValue: SerializedMoney | null;
  provenance: string;
}

export interface ValueLedgerListResponse {
  entries: ValueLedgerEntryDto[];
  summary: {
    total: number;
    expectedByCurrency: SerializedMoney[];
    realizedByCurrency: SerializedMoney[];
    verifiedRealizedByCurrency: SerializedMoney[];
    multipleCurrencies: boolean;
    overlapSuppressedEntries: number;
    verifiedByProvenance: { customerAttested: SerializedMoney[]; systemMeasured: SerializedMoney[]; unverified: SerializedMoney[] };
  };
}

export interface ValueLedgerDetailResponse {
  ledgerEntryId: string;
  valueType: string;
  verificationStatus: string;
  attributionConfidence: string;
  baseline: {
    approach: string | null;
    periodStart: string | null;
    periodEnd: string | null;
    value: SerializedMoney | null;
    normalized: SerializedMoney | null;
  };
  expectedValue: SerializedMoney | null;
  realizedValue: SerializedMoney | null;
  calculationMethod: string | null;
  evidence: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
  attributionExplainer: string;
  workload: { workloadId: string; name: string; applicationId: string } | null;
  action: { actionId: string; title: string; status: string; owner: string | null } | null;
  lineage: {
    recommendation: { recommendationId: string; title: string; source: string; expectedSavings: SerializedMoney | null } | null;
    approvals: Array<{ approvalType: string; status: string; approver: string | null; comments: string | null; resolvedAt: string | null }>;
    implementation: { confirmedBy: string | null; confirmedAt: string | null; actualDate: string | null; evidence: string | null; performedOutsideCoreFinOps: true } | null;
  } | null;
}

// --- Architecture & FinOps --------------------------------------------------
export type ArchNodeType = "business_unit" | "product" | "application" | "environment" | "workload";
export type AllocationState = "allocated" | "partial" | "unallocated" | "zero" | "unavailable";

export interface ArchNodeDto {
  type: ArchNodeType;
  id: string;
  name: string;
  owner: string | null;
  costCenter: string | null;
  environment: string | null;
  criticality: string | null;
  lifecycleState: string | null;
  migrationState: string | null;
  // Authoritative allocation truth (from the latest as-calculated run).
  financialDataAvailable: boolean;
  allocatedByCurrency: SerializedMoney[];
  unallocatedByCurrency: SerializedMoney[];
  allocationState: AllocationState;
  allocationConfidence: string | null;
  multipleCurrencies: boolean;
  // Inventory context (observed resource cost; NOT allocation truth).
  observedResourceCostByCurrency: SerializedMoney[];
  resourceCount: number;
  openFindings: number;
  openActions: number;
  openRecommendations: number;
  verifiedValueByCurrency: SerializedMoney[];
  wave: string | null;
  mappingConfidence: string | null;
  hasChildren: boolean;
}

export interface AllocationRunDto {
  allocationRunId: string;
  billingPeriod: string;
  version: number;
  costMeasure: string;
}

export interface ArchitectureTreeResponse {
  parent: { type: ArchNodeType; id: string } | null;
  nodes: ArchNodeDto[];
  allocationRun: AllocationRunDto | null;
}

export interface ArchNodeDetailResponse {
  node: ArchNodeDto;
  allocationRun: AllocationRunDto | null;
  links: { workloadId: string | null; applicationId: string | null };
}

export interface ArchResourceItem {
  resourceId: string;
  name: string;
  service: string | null;
  resourceType: string | null;
  account: string | null;
  region: string | null;
  environment: string | null;
  workload: string | null;
  application: string | null;
  owner: string | null;
  netAmortizedCost: SerializedMoney | null;
  mappingSource: string | null;
  mappingConfidence: string | null;
  findingsCount: number;
  lifecycleState: string | null;
}

export interface ArchResourceTableResponse {
  items: ArchResourceItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ArchResourceMappingResponse {
  resourceId: string;
  providerResourceId: string | null;
  service: string | null;
  resourceType: string | null;
  region: string | null;
  account: string | null;
  cost: SerializedMoney | null;
  mapping: {
    workloadId: string | null;
    workloadName: string | null;
    applicationId: string | null;
    applicationName: string | null;
    source: string | null;
    confidenceLabel: string | null;
    confidenceScore: string | null;
    ruleId: string | null;
    isPrimary: boolean | null;
    effectiveFrom: string | null;
    effectiveTo: string | null;
  };
  mappingIncomplete: boolean;
  lowConfidence: boolean;
}

// --- Cost Explorer ----------------------------------------------------------
// Mirrors @corefinops/governance ExplorerQuerySpec + services/api explorer-handlers.
export type ExplorerDimension =
  | "account"
  | "service"
  | "region"
  | "tag"
  | "cost_category"
  | "business_unit"
  | "product"
  | "application"
  | "environment"
  | "workload"
  | "owner"
  | "wave";

export type CostMeasure =
  | "unblended_cost"
  | "net_unblended_cost"
  | "amortized_cost"
  | "net_amortized_cost";

export interface ExplorerDateRange {
  start: string; // YYYY-MM-DD inclusive
  end: string; // YYYY-MM-DD exclusive
}

export interface ExplorerFilterSpec {
  dimension: ExplorerDimension;
  key?: string;
  values: string[];
}

export interface ExplorerQuerySpec {
  measure: CostMeasure;
  dateRange: ExplorerDateRange;
  comparisonRange?: ExplorerDateRange;
  groupBy: ExplorerDimension[];
  filters: ExplorerFilterSpec[];
  drilldown?: ExplorerDimension[];
  overlays?: { migration?: boolean; anomaly?: boolean; costChange?: boolean };
  limit?: number;
}

export type FinancialBasis = "native_normalized_facts" | "authoritative_allocation";

export interface ExplorerRowDto {
  keys: Array<string | null>;
  current: SerializedMoney;
  comparison: SerializedMoney | null;
  delta: SerializedMoney | null;
  deltaPercent: string | null;
}

export interface ExplorerAllocationRunRef {
  allocationRunId: string;
  billingPeriod: string;
  version: number;
  costMeasure: string;
}

export interface ExplorerQueryResponse {
  spec: ExplorerQuerySpec;
  financialBasis: FinancialBasis;
  allocationRuns: ExplorerAllocationRunRef[];
  measure: CostMeasure;
  totalsByCurrency: SerializedMoney[];
  comparisonTotalsByCurrency: SerializedMoney[];
  multipleCurrencies: boolean;
  rows: ExplorerRowDto[];
  allocationAvailability: {
    required: boolean;
    available: boolean;
    unavailableForMeasure: boolean;
    missingPeriods: string[];
  };
  historicalBasis: "native" | "allocated_facts" | "rollup" | "historical_evidence" | "historical_dimension_snapshot_unavailable";
  historicalDimensionSnapshotUnavailable: string[];
  unavailableDimensions: string[];
  overlays: {
    migration: { available: boolean };
    costChange: { available: boolean };
    anomaly: { available: boolean };
  };
  limit: number;
  groupBy: string[];
}

export interface SavedViewDto {
  viewId: string;
  name: string;
  spec: ExplorerQuerySpec;
  shared: boolean;
  createdBy: string | null;
  createdAt: string;
  owned: boolean;
}

export interface SavedViewsListResponse {
  views: SavedViewDto[];
}

// --- Reports ----------------------------------------------------------------
export type ReportId =
  | "migration-economics"
  | "decommission"
  | "value-realization"
  | "application-financial-summary"
  | "portfolio-financial-summary"
  | "roi";

export interface ReportSheet {
  name: string;
  columns: string[];
  rows: Array<Array<string | number | null>>;
}

export interface ReportMeta {
  reportId: ReportId;
  title: string;
  tenant: string;
  tenantName: string;
  generatedAt: string;
  financialBasis: string;
  costMeasure: string;
  reportPeriod: string | null;
  allocationRun: { allocationRunId: string; billingPeriod: string; version: number } | null;
  assumptions: string[];
  currencies: string[];
}

export interface ReportDocument {
  meta: ReportMeta;
  sheets: ReportSheet[];
  totalsByCurrency?: SerializedMoney[];
}

export interface ReportsListResponse {
  reports: Array<{ reportId: ReportId; title: string }>;
}

export interface ReportExportResponse {
  reportId: ReportId;
  filename: string;
  contentType: string;
  base64: string;
}

// --- Settings / Admin -------------------------------------------------------
export interface SettingsOverviewResponse {
  tenant: { tenantId: string; name: string; createdAt: string; billingCurrency: string | null };
  financialDefaults: { defaultCostMeasure: string; preservedCostMeasures: string[]; perTenantOverrideSupported: boolean; note: string };
  governance: { budgetsConfigurable: boolean; policiesSurface: string };
  limitations: string[];
}

export interface MembersResponse {
  members: Array<{ userSub: string; email: string | null; roles: string[] }>;
  roleCatalogue: Array<{ role: string; explanation: string }>;
}

export interface AwsConnectionsResponse {
  connections: Array<{
    accountId: string;
    awsAccountId: string;
    accountName: string | null;
    environment: string | null;
    connectionStatus: string;
    billingScope: string | null;
    lastSyncAt: string | null;
    regions: string[];
    dataExportConfigured: boolean;
    validatedAt: string | null;
    validationError: string | null;
    lastInventoryAt: string | null;
    lastFinancialSyncAt: string | null;
  }>;
  credentialStorageNote: string;
}

export interface AllocationPolicyDto {
  allowEqualSplit: boolean;
  allowInferredAllocation: boolean;
  allowAccountDefaultAllocation: boolean;
  adjustmentPolicy: string;
  minConfidenceForAllocation: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AllocationSettingsResponse {
  policy: AllocationPolicyDto;
  isDefault: boolean;
  explanations: Record<string, string>;
  adjustmentPolicyOptions: string[];
}

export interface AuditHistoryResponse {
  events: Array<{
    auditEventId: string;
    entityType: string;
    entityId: string | null;
    actor: string;
    previousState: string | null;
    newState: string;
    occurredAt: string;
  }>;
}

// --- Processing-cost observability ------------------------------------------
export interface IngestionRunTelemetryDto {
  ingestionRunId: string;
  billingPeriod: string | null;
  sourceBytes: number;
  compressedBytes: number | null;
  chunks: number;
  rowsRead: number;
  rowsStaged: number;
  rowsPublished: number;
  rowsRejected: number;
  retries: number;
  failures: number;
  lambdaDurationMs: number | null;
  lambdaMemoryMb: number | null;
  peakMemoryMb: number | null;
  s3GetRequests: number | null;
  athenaScannedBytes: number | null;
  estimatedProcessingCost: string | null;
  currency: string | null;
  costBasis: string | null;
  costPerMillionRows: string | null;
  runStatus: string | null;
  createdAt: string;
}

export interface IngestionObservabilityResponse {
  latest: IngestionRunTelemetryDto | null;
  trend: IngestionRunTelemetryDto[];
  alerts: Array<{ code: string; message: string }>;
  estimatedCostByCurrency: Array<{ amount: string; currency: string }>;
  disclaimer: string;
}

// ---- Commercial SaaS (entitlements, billing, command center, radar, alerts) ----

export interface PlanLimitsDto {
  maxAwsAccounts: number;
  maxMembers: number;
  manualRefreshesPerDay: number;
  radarVisible: number;
  trialDays?: number;
  historicalRetentionMonths: number;
  maxIngestedRowsPerMonth: number;
  exportsPerMonth: number;
  transformationAccess: boolean;
}

export interface CurrentUsageDto {
  awsAccounts: number;
  members: number;
  manualRefreshesToday: number;
  ingestedRowsThisPeriod: number;
  exportsThisPeriod: number;
  reportsThisPeriod: number;
  periodStart: string;
}

export interface ProvenanceDto {
  sourceRecordId: string | null;
  lastVerifiedAt: string | null;
  verificationMethod: string;
}

export interface EntitlementsDto {
  planCode: string;
  planName: string;
  billingSource: "trial" | "direct" | "aws_marketplace" | "internal_demo" | "manual";
  status: string;
  effectiveStatus: "active" | "trialing" | "grace" | "inactive";
  active: boolean;
  isDemo: boolean;
  implicit: boolean;
  limits: PlanLimitsDto;
  features: string[];
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  graceUntil: string | null;
  reason: string | null;
  currentUsage: CurrentUsageDto;
  provenance: ProvenanceDto;
}

export interface MarketplaceBillingDto {
  planName: string;
  status: string;
  effectiveStatus: string;
  expiresAt: string | null;
  graceUntil: string | null;
  purchased: { seats: number | null; awsAccounts: number | null };
  inUse: { seats: number; awsAccounts: number };
  ended: boolean;
  manageUrl: string;
}

export interface BillingOverviewResponse {
  marketplace: MarketplaceBillingDto | null;
  entitlements: EntitlementsDto;
  subscription: { billingSource: string; status: string; providerManaged: boolean; marketplaceManaged: boolean; marketplacePending: boolean; hasBillingAccount: boolean };
  plans: Array<{ planCode: string; name: string; rank: number; limits: PlanLimitsDto; features: string[]; current: boolean; purchasable: boolean }>;
  directBillingAvailable: boolean;
  usage: { awsAccounts: number; members: number; manualRefreshesToday: number };
  channelSwitch: { from: string | null; to: string; state: string } | null;
}

export interface ActivationSnapshotDto {
  milestones: Array<{ milestone: string; reachedAt: string | null }>;
  completed: number;
  total: number;
  freshness: { connectedAccounts: number; incompleteConnections: number; lastCostIngestionAt: string | null; stale: boolean; failedJobsLast24h: number };
}

export interface RadarItemDto {
  recommendationId: string;
  title: string;
  category: string | null;
  source: string;
  expectedSavings: SerializedMoney | null;
  confidence: string | null;
  risk: string | null;
  score: number;
  rank: number;
  stage: "new" | "in_progress" | "verifying";
  actionId: string | null;
  evidence: { findingId: string | null; severity: string | null; observedCondition: string | null; workloadName: string | null };
  overlapKey: string | null;
  overlapsWith: string | null;
  countedInTotal: boolean;
}

export interface SavingsRadarResponse {
  items: RadarItemDto[];
  lockedCount: number;
  potentialByCurrency: SerializedMoney[];
  suppressedByCurrency: SerializedMoney[];
  multipleCurrencies: boolean;
  total: number;
}

export interface NotificationDto {
  notificationId: string;
  kind: string;
  severity: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface CommandCenterResponse {
  persona: "executive" | "finance" | "finops" | "migration" | "admin" | "viewer";
  overview: OverviewKpis & {
    verifiedSavingsByProvenance: { customerAttested: SerializedMoney[]; systemMeasured: SerializedMoney[]; unverified: SerializedMoney[] };
    overlapSuppressedEntries: number;
  };
  activation: ActivationSnapshotDto;
  entitlements: EntitlementsDto;
  topOpportunities: RadarItemDto[];
  opportunities: Pick<SavingsRadarResponse, "potentialByCurrency" | "suppressedByCurrency" | "multipleCurrencies" | "total" | "lockedCount">;
  alerts: NotificationDto[];
  unreadAlerts: number;
  dataQuality: DataQualityDto;
}

export interface DataQualityDto {
  spendBasis: { measure: string; label: string };
  period: string | null;
  dataThrough: string | null;
  lastIngestionAt: string | null;
  stale: boolean;
  coverage: { connectedAccounts: number; accountsWithCost: number; daysWithData: number; daysExpected: number; dayCoverage: string | null };
  missingSources: Array<{ kind: string; message: string; accountName?: string }>;
  basis: "observed";
}

export interface AlertPreferencesDto {
  emailEnabled: boolean;
  minSeverity: "info" | "low" | "medium" | "high" | "critical";
  recipientRoles: string[];
  emailAvailable: boolean;
}

export interface ServiceRequestDto {
  requestId: string;
  topic: string;
  description: string;
  status: string;
  consentShareData: boolean;
  createdAt: string;
}
